import React from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, CircularProgress,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { BlueBtn, RedInverseBtn } from '../Styled/StyledButtons';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import styles from './styles.module.css';
import { CCPhone, LineType, LineTypeDetail } from '../../models/Phones/CCPhone';
import { PhonesActions } from '../../actions/PhonesActions';
import { IError } from '../../models/IError';

const LINE_TYPES: LineType[] = ['landline', 'wireless', 'voip', 'other'];
const LINE_TYPE_DETAILS: LineTypeDetail[] = [
  'landline', 'mobile', 'fixedVoip', 'nonFixedVoip', 'personal',
  'tollFree', 'premium', 'sharedCost', 'uan', 'voicemail', 'pager', 'unknown',
];

interface IProps {
  phone: CCPhone;
  editShow: boolean;
  toggleHandle: () => void;
  savedHandle: () => void;
  onError: (errors: string[]) => void;
}

interface IState {
  phoneInput: string;
  lineType: LineType | '';
  lineTypeDetail: LineTypeDetail | '';
  carrierName: string;
  lookupDate: string;
  saving: boolean;
  phoneInvalid: boolean;
}

export default class PhoneEditComponent extends React.Component<IProps, IState> {

  state: IState = this.buildState(this.props.phone);

  componentDidUpdate(prevProps: IProps) {
    if (this.props.phone !== prevProps.phone || this.props.editShow !== prevProps.editShow) {
      this.setState(this.buildState(this.props.phone));
    }
  }

  buildState(phone: CCPhone): IState {
    return {
      phoneInput: phone.Id ? phone.Phone : '',
      lineType: (phone.LineType ?? '') as LineType | '',
      lineTypeDetail: (phone.LineTypeDetail ?? '') as LineTypeDetail | '',
      carrierName: phone.CarrierName ?? '',
      lookupDate: phone.LookupDate ? phone.LookupDate.split(' ')[0] : '',
      saving: false,
      phoneInvalid: false,
    };
  }

  isCreate(): boolean {
    return this.props.phone.Id === 0;
  }

  save() {
    if (this.isCreate()) {
      if (!this.state.phoneInput.trim()) {
        this.setState({ phoneInvalid: true });
        return;
      }
      this.setState({ saving: true });
      PhonesActions.createPhone(this.state.phoneInput.trim())
        .then((res) => {
          this.setState({ saving: false });
          if (res instanceof CCPhone) {
            this.props.savedHandle();
          } else {
            const e = res as IError;
            this.props.onError(e.errors ?? [e.error]);
          }
        });
    } else {
      this.setState({ saving: true });
      PhonesActions.updatePhone(this.props.phone.Id, {
        line_type: this.state.lineType || undefined,
        line_type_detail: this.state.lineTypeDetail || undefined,
        carrier_name: this.state.carrierName || null,
        lookup_date: this.state.lookupDate
          ? this.state.lookupDate + ' 00:00:00'
          : null,
      } as any)
        .then((res) => {
          this.setState({ saving: false });
          if (res instanceof CCPhone) {
            this.props.savedHandle();
          } else {
            const e = res as IError;
            this.props.onError(e.errors ?? [e.error]);
          }
        });
    }
  }

  render() {
    const { editShow, toggleHandle, phone } = this.props;
    const { saving, phoneInvalid } = this.state;
    const isCreate = this.isCreate();

    return (
      <Dialog open={editShow} onClose={toggleHandle}>
        <DialogTitle>
          {isCreate ? 'Add Phone' : 'Edit Phone: ' + phone.friendly()}
          <IconButton className={mainStyles.floatRight} onClick={toggleHandle}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.popup}>
          <Grid container spacing={1}>
            {isCreate ? (
              <Grid item xs={12}>
                <label className={styles.FormLabel}>Phone number *</label>
                <input
                  placeholder="+1XXXXXXXXXX or (XXX) XXX-XXXX"
                  className={`${styles.FormControl} ${phoneInvalid ? styles.FormInvalid : ''}`}
                  value={this.state.phoneInput}
                  onChange={(e) => this.setState({ phoneInput: e.target.value, phoneInvalid: false })}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <label className={styles.FormLabel}>Phone</label>
                  <input
                    className={styles.FormControl}
                    value={phone.Phone}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'default' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <label className={styles.FormLabel}>Line Type</label>
                  <select
                    className={styles.FormControl}
                    value={this.state.lineType}
                    onChange={(e) => this.setState({ lineType: e.target.value as LineType | '' })}
                  >
                    <option value="">— not set —</option>
                    {LINE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Grid>
                <Grid item xs={6}>
                  <label className={styles.FormLabel}>Line Type Detail</label>
                  <select
                    className={styles.FormControl}
                    value={this.state.lineTypeDetail}
                    onChange={(e) => this.setState({ lineTypeDetail: e.target.value as LineTypeDetail | '' })}
                  >
                    <option value="">— not set —</option>
                    {LINE_TYPE_DETAILS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Grid>
                <Grid item xs={6}>
                  <label className={styles.FormLabel}>Carrier Name</label>
                  <input
                    className={styles.FormControl}
                    placeholder="e.g. T-Mobile"
                    value={this.state.carrierName}
                    onChange={(e) => this.setState({ carrierName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <label className={styles.FormLabel}>Lookup Date</label>
                  <input
                    type="date"
                    className={styles.FormControl}
                    value={this.state.lookupDate}
                    onChange={(e) => this.setState({ lookupDate: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <RedInverseBtn fullWidth onClick={toggleHandle} disabled={saving}>
                Cancel
              </RedInverseBtn>
            </Grid>
            <Grid item xs={6}>
              <BlueBtn fullWidth onClick={() => this.save()} disabled={saving}>
                {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Save'}
              </BlueBtn>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }
}
